import Icon from '@vfz/ui/dist/components/icon/icon';
import { AISearchApi } from 'api';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { getGASessionId } from 'utils/analytics';
import { ContentUtil } from 'utils/content';

import { content } from './ai-search.content';
import * as S from './ai-search.styles';

interface AIFeedbackProps {
  searchId: string | null;
}

const contentUtil = new ContentUtil({ content, componentName: 'AIFeedback' });

export const AIFeedback = ({ searchId }: AIFeedbackProps) => {
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [showExtendsedForm, setShowExtendedForm] = useState(false);
  const [likeActive, setLikeActive] = useState(false);
  const [dislikeActive, setDislikeActive] = useState(false);
  const feedbackTextarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    feedbackTextarea?.current?.focus();
  }, [showExtendsedForm]);

  const handleFeedback = (isCorrect: boolean) => (event: MouseEvent) => {
    event.preventDefault();

    setShowExtendedForm(true);

    setLikeActive(isCorrect);
    setDislikeActive(!isCorrect);

    postFeedback(searchId, { isCorrect });
  };

  const handleExtendedFeedback = async (values: Record<string, string>): Promise<void> => {
    if (!values.comment || !values.comment.trim().length) return;

    setShowExtendedForm(false);
    setHasSubmittedFeedback(true);

    await postFeedback(searchId, {
      comment: values.comment,
    });
  };

  const postFeedback = async (
    searchId: string | null,
    data: {
      isCorrect?: boolean;
      comment?: string;
    }
  ) => {
    try {
      if (!searchId) {
        // eslint-disable-next-line no-console
        console.error('Search ID is required to post feedback');
        return;
      }

      const sessionId = getGASessionId();
      await AISearchApi.postSearchResultFeedback(searchId, sessionId, {
        is_correct: data.isCorrect,
        comment: data.comment,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error posting feedback:', e);
    }
  };

  if (hasSubmittedFeedback) {
    return (
      <S.SearchFeedbackSuccess data-testid="ai-search-feedback-success">
        {contentUtil.translateToString('feedbackSuccess')}
      </S.SearchFeedbackSuccess>
    );
  }

  return (
    <div data-testid="ai-search-feedback">
      <S.SearchFeedbackWrapper>
        <p>{contentUtil.translateToString('feedback')}</p>
        <S.FeedbackButtonIcon
          type="button"
          data-testid="like-feedback"
          variant={S.ButtonVariant.SUCCESS}
          active={likeActive}
          onClick={handleFeedback(true)}>
          <Icon name="like" aria-hidden="true" />
          <S.ScreenreaderOnly>{contentUtil.translateToString('feedbackThumbsUp')}</S.ScreenreaderOnly>
        </S.FeedbackButtonIcon>
        <S.FeedbackButtonIcon
          type="button"
          data-testid="dislike-feedback"
          variant={S.ButtonVariant.ERROR}
          active={dislikeActive}
          onClick={handleFeedback(false)}>
          <Icon name="dislike" aria-hidden="true" />
          <S.ScreenreaderOnly>{contentUtil.translateToString('feedbackThumbsDown')}</S.ScreenreaderOnly>
        </S.FeedbackButtonIcon>
      </S.SearchFeedbackWrapper>
      {showExtendsedForm && (
        <S.ExtendedFeedbackForm
          onSubmit={handleExtendedFeedback}
          skipScrollToRef
          testid="extended-feedback-form"
          hideCancelButton
          buttonText={contentUtil.translateToString('submitFeedback')}
          dotTheme="primary"
          render={() => (
            <>
              <S.SearchLabelFeedback htmlFor="search-feedback">
                {contentUtil.translateToString('feedbackLabel')}
              </S.SearchLabelFeedback>
              <S.SearchFeedback
                maxLength={500}
                rows={2}
                id="search-feedback"
                name="comment"
                textareaRef={feedbackTextarea}
              />
            </>
          )}
        />
      )}
    </div>
  );
};
